import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Select, InputNumber, Button, Spin, message } from "antd";
import { LoadingOutlined, MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { svg } from "../../../../Assets/svg";
import {
  ATTRIBUTE_ORDER,
  capitalizeFirstLetter,
} from "../../../../helpers/utils";
import { useAppDispatch } from "../../../../store";
import {
  getPossibleDimesionsAPI,
  getVariantPriceAPI,
} from "../../Quotations/quotationApis";
import {
  ProductDropEnum,
  ProductDropLabels,
  ProductFinishEnum,
  ProductFinishLabels,
} from "../../Products/productsTypes";

interface Props {
  productItem: any;
  onCancel: () => void;
  onVariantResolved: (variant: any, qty: number) => void;
  initialSelections?: Record<string, any>;
}

export default function BOMProductConfigurator({
  productItem,
  onCancel,
  onVariantResolved,
  initialSelections,
}: Props) {
  const dispatch = useAppDispatch();

  const [selections, setSelections] = useState<Record<string, any>>(initialSelections || {});
  const [options, setOptions] = useState<Record<string, any[]>>({});
  const [isStandardProduct, setIsStandardProduct] = useState<any>([]);
  const [standardOptionsData, setStandardOptionsData] = useState<Record<string, any[]>>({});
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [isCalculatingPrice, setIsCalculatingPrice] = useState(false);
  const [qty, setQty] = useState(1);
  const [activeVariables, setActiveVariables] = useState<any>(productItem.variables || {});

  useEffect(() => {
    if (productItem.variables) {
      setActiveVariables(productItem.variables);
    }
  }, [productItem.variables]);

  const hasVariables = useMemo(
    () => Object.keys(activeVariables || {}).length > 0,
    [activeVariables],
  );

  const isFormValid = useMemo(() => {
    if (!hasVariables) return true;
    const requiredKeys = ATTRIBUTE_ORDER.filter((attr: any) => {
      if (attr.key === "color")
        return selections["finish"] === ProductFinishEnum.POWDER_COATED;
      if (attr.key === "custom_drop")
        return (
          selections["drop"] === "custom" ||
          selections["drop"] === ProductDropEnum.CUSTOM
        );
      return activeVariables?.[attr.key];
    }).map((attr: any) => attr.key);
    return requiredKeys.every(
      (key: any) => selections[key] !== undefined && selections[key] !== null,
    );
  }, [activeVariables, selections, hasVariables]);

  const handleSave = useCallback(() => {
    if (!isFormValid) return;

    setIsCalculatingPrice(true);

    const sanitizedSelections = { ...selections };
    if (sanitizedSelections["finish"] !== ProductFinishEnum.POWDER_COATED) {
      delete sanitizedSelections["color"];
    }

    const isCustomDrop =
      sanitizedSelections["drop"] === "custom" ||
      sanitizedSelections["drop"] === ProductDropEnum.CUSTOM;

    if (!isCustomDrop) {
      delete sanitizedSelections["custom_drop"];
    }

    const payload = {
      product_id: productItem.product_id || productItem._id,
      qty: qty,
      use_quote_price: false,
      variants: [sanitizedSelections],
    };

    dispatch(
      getVariantPriceAPI({
        payload,
        onSuccess: (data: any) => {
          setIsCalculatingPrice(false);
          const pricingData = data?.variants?.[0]?.pricing;
          const variantData = data?.variants?.[0];

          if (!pricingData?.variant_id) {
            message.error("Unable to retrieve variant ID.");
            return;
          }

          onVariantResolved(
            {
              _id: pricingData.variant_id,
              variant_id: pricingData.variant_id,
              variant_name: pricingData.variant_name || productItem.name,
              sku: pricingData.sku,
              attributes: sanitizedSelections,
              productData: productItem,
              ...variantData, // include everything resolved from backend
            },
            qty
          );
        },
        onError: () => {
          message.error("Failed to calculate configuration.");
          setIsCalculatingPrice(false);
        },
      }),
    );
  }, [
    dispatch,
    productItem,
    qty,
    selections,
    isFormValid,
    onVariantResolved,
  ]);

  const fetchDimensionOptions = useCallback(
    (currentFilters: Record<string, any>, triggeredKey?: string | null) => {
      setIsLoadingOptions(true);
      if (!triggeredKey) {
        setOptions({});
      }
      const payload = {
        product_id: productItem.product_id || productItem._id,
        ...currentFilters,
      };

      dispatch(
        getPossibleDimesionsAPI({
          filters: payload,
          onSuccess: (data: any) => {
            let newOptions = data?.possible_values || {};
            if (
              data?.is_standard &&
              data?.with_standard_options_possible_values
            ) {
              const orderedOptions: Record<string, any[]> = {};
              Object.keys(data.with_standard_options_possible_values).forEach(
                (k) => {
                  const arr = data.with_standard_options_possible_values[k];
                  if (
                    Array.isArray(arr) &&
                    arr.length > 0 &&
                    typeof arr[0] === "object" &&
                    "value" in arr[0]
                  ) {
                    orderedOptions[k] = arr.map((item: any) => item.value);
                  } else {
                    orderedOptions[k] = newOptions[k];
                  }
                },
              );
              newOptions = { ...newOptions, ...orderedOptions };
            }
            if (data?.variables) {
              setActiveVariables(data.variables);
            }
            const result = data?.is_standard
              ? Object.keys(data?.standard_options).filter(
                  (key) => data?.standard_options[key].length > 0,
                )
              : [];

            setIsStandardProduct(result);

            setStandardOptionsData((prevData) => {
              const newData = data?.with_standard_options_possible_values || {};
              if (!triggeredKey) return newData;

              const changeIndex = ATTRIBUTE_ORDER.findIndex((attr: any) => attr.key === triggeredKey);
              if (changeIndex === -1) return newData;

              const mergedData = { ...newData };
              ATTRIBUTE_ORDER.forEach((attr: any, index: number) => {
                if (index <= changeIndex && prevData[attr.key]) {
                  mergedData[attr.key] = prevData[attr.key];
                }
              });
              return mergedData;
            });

            // AUTO-SELECT LOGIC
            if (!triggeredKey) {
              const nextSelections = { ...selections };

              ATTRIBUTE_ORDER.forEach((attr: any) => {
                const isActive = data?.variables?.[attr.key];
                if (isActive) {
                  const vals = newOptions[attr.key] || [];
                  if (vals.length === 1 && !nextSelections[attr.key]) {
                    nextSelections[attr.key] = vals[0];
                  }
                }
              });

              setSelections(nextSelections);
            }

            setOptions((prevOptions) => {
              if (!triggeredKey) return newOptions;
              const changeIndex = ATTRIBUTE_ORDER.findIndex((attr: any) => attr.key === triggeredKey);
              if (changeIndex === -1) return newOptions;
              const mergedOptions: Record<string, any[]> = { ...newOptions };
              ATTRIBUTE_ORDER.forEach((attr: any, index: number) => {
                if (index <= changeIndex && prevOptions[attr.key]) {
                  mergedOptions[attr.key] = prevOptions[attr.key];
                }
              });
              return mergedOptions;
            });

            if (triggeredKey) {
              const changeIndex = ATTRIBUTE_ORDER.findIndex((attr: any) => attr.key === triggeredKey);
              if (changeIndex !== -1) {
                setSelections((prev) => {
                  const next = { ...prev };
                  let hasPruned = false;
                  ATTRIBUTE_ORDER.slice(changeIndex + 1).forEach((attr: any) => {
                    const val = next[attr.key];
                    const possibleVals = newOptions[attr.key] || [];
                    if (
                      val !== undefined &&
                      val !== null &&
                      val !== "" &&
                      !possibleVals.includes(val)
                    ) {
                      delete next[attr.key];
                      hasPruned = true;
                    }

                    if (
                      (next[attr.key] === undefined ||
                        next[attr.key] === null ||
                        next[attr.key] === "") &&
                      possibleVals.length === 1
                    ) {
                      next[attr.key] = possibleVals[0];
                      hasPruned = true;
                    }
                  });
                  return hasPruned ? next : prev;
                });
              }
            }
            setIsLoadingOptions(false);
          },
          onError: () => {
            setIsLoadingOptions(false);
          },
        }),
      );
    },
    [dispatch, productItem._id, productItem.product_id, selections],
  );

  // INITIALIZATION
  useEffect(() => {
    fetchDimensionOptions({}, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAttributeChange = (key: string, value: any) => {
    const currentIdx = ATTRIBUTE_ORDER.findIndex((a: any) => a.key === key);
    const updatedSelections = { ...selections, [key]: value };
    setSelections(updatedSelections);
    const filterPayload: Record<string, any> = {};
    ATTRIBUTE_ORDER.slice(0, currentIdx + 1).forEach((attr: any) => {
      if (updatedSelections[attr.key] !== undefined) {
        filterPayload[attr.key] = updatedSelections[attr.key];
      }
    });
    fetchDimensionOptions(filterPayload, key);
  };

  const getFormattedOptions = (key: string, rawValues: any[]) => {
    const currentVal = selections[key];
    const uniqueValues = [...(rawValues || [])];
    if (
      currentVal !== undefined &&
      currentVal !== null &&
      !uniqueValues.includes(currentVal)
    ) {
      uniqueValues.push(currentVal);
    }
    
    if (uniqueValues.length === 0) return [];
    return uniqueValues.map((val) => {
      let labelStr = val.toString();

      if (key === "finish") {
        labelStr =
          ProductFinishLabels[val as ProductFinishEnum] ||
          val.toString().split("_").map(capitalizeFirstLetter).join(" ");
      } else if (key === "drop") {
        labelStr =
          ProductDropLabels[val as ProductDropEnum] ||
          val.toString().split("_").map(capitalizeFirstLetter).join(" ");
      } else if (typeof val === "string") {
        labelStr = val.split("_").map(capitalizeFirstLetter).join(" ");
      }

      let finalLabel: React.ReactNode = labelStr;
      const isFieldApplicable = key !== "drop";
      let tags: any[] = [];
      let isStandardValue = false;
      let hasStandardConfig = false;

      if (isFieldApplicable && standardOptionsData[key]) {
        const itemData = standardOptionsData[key].find(
          (item: any) => String(item.value) === String(val),
        );
        if (itemData) {
          tags = itemData.tags || [];
          isStandardValue = itemData.value_is_standard;
        }

        hasStandardConfig =
          isStandardProduct?.length > 0 && isStandardProduct?.includes(key);
      }

      if (hasStandardConfig || tags.length > 0) {
        finalLabel = (
          <div className="productItemRow d-flex flex-wrap align-items-center gap-2">
            <span className="text-truncate itemFullLabel">{labelStr}</span>
            {hasStandardConfig && (
              <span
                className="itemBadge fs-8 px-2 py-1 fw-normal rounded-2 lh-normal flex-shrink-0"
                style={{
                  backgroundColor: isStandardValue ? "#E9FFE7" : "#FFFAED",
                  color: isStandardValue ? "#419F20" : "#FFB400",
                }}
              >
                {isStandardValue ? "Standard" : "Non-Standard"}
              </span>
            )}
            {tags.length > 0 &&
              tags.map((tag: any, index: number) => (
                <span
                  key={index}
                  className="itemBadge badge rounded-pill flex-shrink-0 mw-100 text-start text-wrap"
                  style={{
                    backgroundColor: tag.tag_color || "#231f20",
                    color: "#fff",
                  }}
                >
                  {tag.tag_name}
                </span>
              ))}
          </div>
        );
      }

      return { value: val, label: finalLabel };
    });
  };

  const antIcon = <LoadingOutlined style={{ fontSize: 16, color: "#ffffff" }} spin />;

  return (
    <div className="bg-light rounded-3 p-3 mt-3 border border-secondary-subtle">
      <div className="row align-items-center gy-2">
        <div className="col-sm-6 order-sm-1 order-1">
          <h6 className="fw-semibold fs-5 mb-0">Select Details</h6>
        </div>
        <div className="col-sm-6 order-sm-2 order-3">
          <div className="row gx-md-4 gx-2 justify-content-end">
            <div className="col-sm-auto col-6">
              <button
                onClick={onCancel}
                className="btn btn-outline-secondary btn-lg mob-btn px-3 fw-bold w-100 text-uppercase"
                disabled={isCalculatingPrice}
              >
                CANCEL
              </button>
            </div>
            <div className="col-sm-auto col-6">
              <button
                onClick={handleSave}
                className="btn btn-primary btn-lg mob-btn px-4 w-100 text-uppercase"
                style={{ backgroundColor: "#212529" }}
                disabled={!isFormValid || isCalculatingPrice}
              >
                {isCalculatingPrice ? (
                  <Spin indicator={antIcon} size="small" />
                ) : (
                  "ADD TO BOM"
                )}
              </button>
            </div>
          </div>
        </div>
        <div className="col-md-12 order-sm-3 order-2 mt-3">
          {isLoadingOptions && Object.keys(options).length === 0 ? (
            <div className="d-flex justify-content-center align-items-center py-4 w-100">
              <Spin size="large" />
            </div>
          ) : (
            <div className="row gy-md-3 gy-2 page-form">
              {ATTRIBUTE_ORDER.map((attr: any, index: number) => {
                const isColor = attr.key === "color";
                const isCustomDrop = attr.key === "custom_drop";

                if (!isColor && !isCustomDrop && !activeVariables?.[attr.key]) {
                  return null;
                }

                if (isColor && selections["finish"] !== ProductFinishEnum.POWDER_COATED) {
                  return null;
                }

                if (isCustomDrop) {
                  const isCustom =
                    selections["drop"] === "custom" ||
                    selections["drop"] === ProductDropEnum.CUSTOM;
                  if (!isCustom) return null;
                }

                let isDisabled = isLoadingOptions || isCalculatingPrice;
                if (index > 0) {
                  const prevEnabledAttrs = ATTRIBUTE_ORDER.slice(0, index).filter((a: any) => {
                    if (a.key === "color") {
                      return selections["finish"] === ProductFinishEnum.POWDER_COATED;
                    }
                    if (a.key === "custom_drop") {
                      return (
                        selections["drop"] === "custom" ||
                        selections["drop"] === ProductDropEnum.CUSTOM
                      );
                    }
                    return activeVariables?.[a.key];
                  });
                  if (prevEnabledAttrs.length > 0) {
                    const lastPrev = prevEnabledAttrs[prevEnabledAttrs.length - 1];
                    if (
                      selections[lastPrev.key] === undefined ||
                      selections[lastPrev.key] === null ||
                      selections[lastPrev.key] === ""
                    )
                      isDisabled = true;
                  }
                }

                const fieldOptions = getFormattedOptions(attr.key, options[attr.key] || []);
                return (
                  <div className={`${attr.key === "finish" ? "col-md-6" : "col-md-3 col-sm-6"}  ${fieldOptions?.length > 1 || attr.key === "color" ? "" : "d-none"}`} key={attr.key}>
                    <label className="small fw-bold mb-1">{attr.label}</label>
                    <Select
                      placeholder="Select"
                      className="w-100 auto-height-select"
                      popupClassName="full-text-select-dropdown"
                      showSearch
                      suffixIcon={
                        isDisabled && isLoadingOptions ? (
                          <Spin size="small" />
                        ) : (
                          svg?.selectDownArrow
                        )
                      }
                      value={selections[attr.key]}
                      onChange={(val) => handleAttributeChange(attr.key, val)}
                      disabled={isDisabled}
                      options={fieldOptions}
                      notFoundContent={
                        isLoadingOptions ? <Spin size="small" /> : "No Options"
                      }
                    />
                  </div>
                );
              })}

              <div className="col-md-3 col-sm-6">
                <label className="small fw-bold mb-1">Qty</label>
                <div className="form-control form-control-lg d-flex align-items-center justify-content-between p-1 itemAddRemoveInput">
                  <Button
                    type="text"
                    icon={<MinusOutlined />}
                    className="btn btn-primary addRemoveBtn"
                    onClick={() => setQty(Math.max(1, qty - 1))}
                    disabled={isCalculatingPrice}
                  />
                  <InputNumber
                    min={1}
                    onWheel={(e) => e.currentTarget.blur()}
                    value={qty}
                    onChange={(val) => setQty(val || 1)}
                    className="border-0 text-center w-100 shadow-none"
                    controls={false}
                    disabled={isCalculatingPrice}
                  />
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    className="btn btn-primary addRemoveBtn"
                    onClick={() => setQty(qty + 1)}
                    disabled={isCalculatingPrice}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
